import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ImagePlus, Loader2, Flame, ChevronLeft, ChevronRight, Download, Printer } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { Panel } from "@/components/AppShell";
import { nbaToday, shortDate } from "@/lib/format";
import { useSlate } from "@/hooks/useSlate";
import { GamesPanel } from "@/components/triple/GamesPanel";
import { GameCenter } from "@/components/triple/GameCenter";
import { EuroGameCenter } from "@/components/triple/EuroGameCenter";
import { MARKET_LABEL, SCAN_MARKETS, pct, evText } from "@/components/value/valueFormat";
import "@/components/value/ValueScanner.css";
import "@/components/triple/TripleView.css";
import "./HalfTimeValue.css";

const TYPES = ["image/png", "image/jpeg", "image/webp"];
const MARKET_TYPE = { moneyline: "Νικητής", handicap: "Χάντικαπ", total: "Over/Under" };
const shift = (day, n) => { const d = new Date(`${day}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };

function Dropzone({ busy, onFiles, hint, testid }) {
  const [over, setOver] = useState(false);
  const input = useRef(null);
  const depth = useRef(0);
  const drag = {
    onDragEnter: (e) => { e.preventDefault(); depth.current += 1; setOver(true); },
    onDragLeave: () => { depth.current -= 1; if (depth.current <= 0) { depth.current = 0; setOver(false); } },
    onDragOver: (e) => e.preventDefault(),
    onDrop: (e) => { e.preventDefault(); depth.current = 0; setOver(false); onFiles(e.dataTransfer.files); },
  };
  return (
    <div className="vs-upload" style={{ marginTop: 14 }}>
      <div role="button" tabIndex={0} className={`vs-drop ${over ? "is-over" : ""} ${busy ? "is-busy" : ""}`} {...drag}
        onClick={() => !busy && input.current?.click()} onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && !busy && input.current?.click()}
        data-testid={testid}>
        <input ref={input} type="file" accept={TYPES.join(",")} multiple hidden onChange={(e) => { onFiles(e.target.files); if (input.current) input.current.value = ""; }} data-testid={`${testid}-input`} />
        {busy ? <Loader2 size={26} className="vs-spin" /> : <ImagePlus size={26} />}
        <b>{busy ? "Διαβάζω τα odds…" : over ? "Άφησε για σάρωση" : "Σύρε & άφησε live screenshot εδώ"}</b>
        <span>ή κλικ · ή Ctrl+V · έως 6 εικόνες · {hint}</span>
      </div>
    </div>
  );
}

function usePaste(sendRef) {
  useEffect(() => {
    const onPaste = (e) => { const f = [...(e.clipboardData?.files || [])].filter((x) => TYPES.includes(x.type)); if (f.length) { e.preventDefault(); sendRef.current(f); } };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [sendRef]);
}

function PlayersMode({ comp, gameId, gameLabel }) {
  const [market, setMarket] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState([]);
  const [meta, setMeta] = useState(null);
  const [tab, setTab] = useState("upload");

  const send = async (list) => {
    if (busy) return;
    const files = [...list].filter((f) => TYPES.includes(f.type)).slice(0, 6);
    if (!files.length) return toast.error("PNG, JPG ή WEBP screenshots μόνο");
    const form = new FormData();
    files.forEach((f, i) => form.append("files", f, f.name || `shot-${i}.png`));
    if (market) form.append("market", market);
    if (comp) form.append("comp", comp);
    if (gameId) form.append("game_id", gameId);
    setBusy(true);
    try {
      const res = await api.post("/value/scan-live", form, { timeout: 180000 });
      const incoming = res.data.results || [];
      // Accumulate across uploads: keep previous markets/players, replace only the same player in the same market.
      setResults((prev) => {
        const map = new Map(prev.map((g) => [`${g.market}::${g.player}`, g]));
        incoming.forEach((g) => map.set(`${g.market}::${g.player}`, g));
        return [...map.values()];
      });
      setMeta(res.data);
      setTab("results");
      toast.success(`${res.data.scanned} τιμές · ${res.data.value_count} value (${res.data.live_games} live αγώνες)`);
    } catch (e) { toast.error(errorText(e)); }
    finally { setBusy(false); }
  };
  const sendRef = useRef(send); sendRef.current = send;
  usePaste(sendRef);

  const byMarket = useMemo(() => {
    const order = ["pts", "reb", "ast", "fg3m", "pra"];
    const map = {};
    results.forEach((g) => { (map[g.market] = map[g.market] || []).push(g); });
    return Object.keys(map)
      .sort((a, b) => order.indexOf(a) - order.indexOf(b))
      .map((m) => ({ market: m, label: map[m][0].market_label, groups: [...map[m]].sort((a, b) => (b.lines.some((l) => l.value) - a.lines.some((l) => l.value))) }));
  }, [results]);
  const valueCount = useMemo(() => results.reduce((a, g) => a + g.lines.filter((l) => l.value).length, 0), [results]);

  return (
    <>
      <div className="tv-tabs htv-subtabs">
        <button type="button" className={tab === "upload" ? "is-active" : ""} onClick={() => setTab("upload")} data-testid="htv-tab-upload">Ανέβασμα</button>
        <button type="button" className={tab === "results" ? "is-active" : ""} onClick={() => setTab("results")} data-testid="htv-tab-results">Προτάσεις{results.length ? ` · ${results.length}` : ""}</button>
      </div>
      {tab === "upload" && (
        <div className="htv-pane">
          <p className="htv-intro">Ανέβασε screenshot με τα live <b>prop odds παικτών</b> στο ημίχρονο{gameLabel ? ` για ${gameLabel}` : ""}. Υπολογίζω τι έχει ήδη πετύχει ο παίκτης + πρόβλεψη για το <b>υπόλοιπο μισό</b>, με τα ίδια κριτήρια value όπως στο pregame. Κάθε νέο screenshot <b>προστίθεται</b> στα προηγούμενα ανά market.</p>
          <label className="vs-select">Market
            <select value={market} onChange={(e) => setMarket(e.target.value)} disabled={busy} data-testid="htv-market-select">
              <option value="">Αυτόματο (από screenshot)</option>
              {SCAN_MARKETS.map((k) => <option key={k} value={k}>{MARKET_LABEL[k]}</option>)}
            </select>
          </label>
          <Dropzone busy={busy} onFiles={send} hint={market ? MARKET_LABEL[market] : "Αυτόματο"} testid="htv-dropzone" />
        </div>
      )}
      {tab === "results" && (
        <div data-testid="htv-result" className="htv-pane space-y-4">
          {!meta && !results.length ? <p className="tv-empty">Ανέβασε screenshot για να δεις προτάσεις.</p> : <>
            {results.length > 0 && (
              <div className="htv-acc-bar" data-testid="htv-acc-bar">
                <span>{results.length} παίκτες · {byMarket.length} markets · <b className="htv-pos">{valueCount}</b> value</span>
                <button type="button" className="htv-clear-btn" onClick={() => { setResults([]); setMeta(null); }} data-testid="htv-clear">Καθαρισμός</button>
              </div>
            )}
            {(meta?.warnings || []).map((w, i) => <p key={i} className="htv-warn">⚠ {w}</p>)}
            {!results.length ? (
              <p className="tv-empty" data-testid="htv-empty">Καμία γραμμή δεν αντιστοιχήθηκε σε live παίκτη. {meta?.skipped?.length ? `(${meta.skipped.length} παραλείφθηκαν)` : ""}</p>
            ) : byMarket.map((sec) => (
              <section key={sec.market} className="htv-market" data-testid={`htv-market-${sec.market}`}>
                <h3 className="htv-market-title"><span className="htv-market-chip">{sec.label}</span><span className="htv-market-count">{sec.groups.length} παίκτες</span></h3>
                {sec.groups.map((g, i) => (
                  <Panel key={`${sec.market}-${i}`} className="p-4 htv-card" data-testid={`htv-group-${sec.market}-${i}`}>
                    <div className="htv-head">
                      <div><b className="htv-name">{g.player}</b><span className="htv-sub">{g.team} vs {g.opp}</span></div>
                      <div className="htv-now">
                        <span>Τώρα <b>{g.current}</b></span>
                        <span>Πρόβλεψη τελικού {sec.label} <b>{g.proj_final}</b></span>
                      </div>
                    </div>
                    <div className="htv-table-wrap">
                      <table className="htv-table">
                        <thead><tr><th>Γραμμή ({sec.label})</th><th>Odds</th><th>Πιθανότητα</th><th>EV</th><th></th></tr></thead>
                        <tbody>
                          {g.lines.map((l, j) => (
                            <tr key={j} className={l.value ? "htv-value" : ""} data-testid={`htv-line-${sec.market}-${i}-${j}`}>
                              <td>{l.label || `${l.side === "under" ? "U" : "O"} ${l.line} ${sec.label}`}</td>
                              <td>{l.odds.toFixed(2)}</td>
                              <td>{pct(l.p)}</td>
                              <td className={l.ev >= 0 ? "htv-pos" : "htv-neg"}>{evText(l.ev)}</td>
                              <td>{l.value ? <span className="htv-badge" data-testid={`htv-value-badge-${sec.market}-${i}-${j}`}><Flame size={11} /> VALUE</span> : null}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Panel>
                ))}
              </section>
            ))}
            {meta?.skipped?.length > 0 && (
              <Panel className="p-4 htv-skipped" data-testid="htv-skipped">
                <b>Παραλείφθηκαν στο τελευταίο ανέβασμα ({meta.skipped.length})</b>
                <ul>{meta.skipped.slice(0, 20).map((s, i) => <li key={i}>{s.prop} — {s.reason}</li>)}</ul>
              </Panel>
            )}
          </>}
        </div>
      )}
    </>
  );
}

function TeamsMode({ comp, gameId, gameLabel }) {
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState([]);
  const [meta, setMeta] = useState(null);
  const [tab, setTab] = useState("upload");

  const send = async (list) => {
    if (busy) return;
    const files = [...list].filter((f) => TYPES.includes(f.type)).slice(0, 6);
    if (!files.length) return toast.error("PNG, JPG ή WEBP screenshots μόνο");
    const form = new FormData();
    files.forEach((f, i) => form.append("files", f, f.name || `shot-${i}.png`));
    if (comp) form.append("comp", comp);
    if (gameId) form.append("game_id", gameId);
    setBusy(true);
    try {
      const res = await api.post("/value/scan-live-team", form, { timeout: 180000 });
      const incoming = res.data.results || [];
      // Accumulate across uploads: keep previous games/markets, merge new markets into the same game.
      setResults((prev) => {
        const map = new Map(prev.map((g) => [`${g.away_code}@${g.home_code}`, g]));
        incoming.forEach((ng) => {
          const k = `${ng.away_code}@${ng.home_code}`;
          const ex = map.get(k);
          if (!ex) { map.set(k, ng); return; }
          const mkey = (m) => `${m.type}::${m.label}`;
          const mm = new Map((ex.markets || []).map((m) => [mkey(m), m]));
          (ng.markets || []).forEach((m) => mm.set(mkey(m), m));
          map.set(k, { ...ex, ...ng, markets: [...mm.values()] });
        });
        return [...map.values()];
      });
      setMeta(res.data);
      setTab("results");
      toast.success(`${res.data.results.length} αγώνες · ${res.data.value_count} value markets`);
    } catch (e) { toast.error(errorText(e)); }
    finally { setBusy(false); }
  };
  const sendRef = useRef(send); sendRef.current = send;
  usePaste(sendRef);

  const valueCount = useMemo(() => results.reduce((a, g) => a + (g.markets || []).filter((m) => m.value).length, 0), [results]);

  return (
    <>
      <div className="tv-tabs htv-subtabs">
        <button type="button" className={tab === "upload" ? "is-active" : ""} onClick={() => setTab("upload")} data-testid="htv-team-tab-upload">Ανέβασμα</button>
        <button type="button" className={tab === "results" ? "is-active" : ""} onClick={() => setTab("results")} data-testid="htv-team-tab-results">Προτάσεις{results.length ? ` · ${results.length}` : ""}</button>
      </div>
      {tab === "upload" && (
        <div className="htv-pane">
          <p className="htv-intro">Ανέβασε screenshot με τα live <b>ομαδικά markets</b> (Νικητής · Χάντικαπ · Over/Under) στο ημίχρονο{gameLabel ? ` για ${gameLabel}` : ""}. Παίρνω το τρέχον σκορ, προβάλλω τον υπόλοιπο αγώνα με τον τρέχοντα ρυθμό, και δείχνω το EV & value κάθε market. Κάθε νέο screenshot <b>προστίθεται</b> στα προηγούμενα markets του αγώνα.</p>
          <Dropzone busy={busy} onFiles={send} hint="Ομαδικά markets" testid="htv-team-dropzone" />
        </div>
      )}
      {tab === "results" && (
        <div data-testid="htv-team-result" className="htv-pane space-y-4">
          {!meta && !results.length ? <p className="tv-empty">Ανέβασε screenshot για να δεις προτάσεις.</p> : <>
            {results.length > 0 && (
              <div className="htv-acc-bar" data-testid="htv-team-acc-bar">
                <span>{results.length} αγώνες · <b className="htv-pos">{valueCount}</b> value markets</span>
                <button type="button" className="htv-clear-btn" onClick={() => { setResults([]); setMeta(null); }} data-testid="htv-team-clear">Καθαρισμός</button>
              </div>
            )}
            {(meta?.warnings || []).map((w, i) => <p key={i} className="htv-warn">⚠ {w}</p>)}
            {!results.length ? (
              <p className="tv-empty" data-testid="htv-team-empty">Κανένα ομαδικό market δεν αντιστοιχήθηκε σε live αγώνα. {meta?.skipped?.length ? `(${meta.skipped.length} παραλείφθηκαν)` : ""}</p>
            ) : results.map((g, i) => (
              <Panel key={i} className="p-4 htv-card" data-testid={`htv-team-group-${i}`}>
                <div className="htv-head">
                  <div><b className="htv-name">{g.away_code} @ {g.home_code}</b><span className="htv-sub">{g.status_detail || "Live"} · Τρέχον {g.away_pts}–{g.home_pts}</span></div>
                  <div className="htv-now">
                    <span>Πρόβλεψη τελικού <b>{g.proj.away}–{g.proj.home}</b></span>
                    <span>Σύνολο <b>{g.proj.total}</b></span>
                    <span>Υπόλοιπο <b>{g.proj.remaining_min}′</b></span>
                  </div>
                </div>
                <div className="htv-table-wrap">
                  <table className="htv-table">
                    <thead><tr><th>Market</th><th>Επιλογή</th><th>Odds</th><th>Πιθανότητα</th><th>EV</th><th></th></tr></thead>
                    <tbody>
                      {g.markets.map((m, j) => (
                        <tr key={j} className={m.value ? "htv-value" : ""} data-testid={`htv-team-line-${i}-${j}`}>
                          <td className="htv-mtype">{MARKET_TYPE[m.type] || m.type}</td>
                          <td>{m.label}</td>
                          <td>{m.odds.toFixed(2)}</td>
                          <td>{pct(m.p)}</td>
                          <td className={m.ev >= 0 ? "htv-pos" : "htv-neg"}>{evText(m.ev)}</td>
                          <td>{m.value ? <span className="htv-badge" data-testid={`htv-team-value-badge-${i}-${j}`}><Flame size={11} /> VALUE</span> : null}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>
            ))}
            {meta?.skipped?.length > 0 && (
              <Panel className="p-4 htv-skipped" data-testid="htv-team-skipped">
                <b>Παραλείφθηκαν στο τελευταίο ανέβασμα ({meta.skipped.length})</b>
                <ul>{meta.skipped.slice(0, 20).map((s, i) => <li key={i}>Screenshot {s.shot} — {s.reason}</li>)}</ul>
              </Panel>
            )}
          </>}
        </div>
      )}
    </>
  );
}

function ProfitChart({ points, unit, label }) {
  if (points.length < 2) return null;
  const W = 300, H = 78, P = 6;
  const xs = points.map((_, i) => P + (i * (W - 2 * P)) / (points.length - 1));
  const vals = points.map((p) => p.value);
  const lo = Math.min(0, ...vals), hi = Math.max(0, ...vals);
  const span = (hi - lo) || 1;
  const y = (v) => H - P - ((v - lo) / span) * (H - 2 * P);
  const line = points.map((p, i) => `${i ? "L" : "M"}${xs[i].toFixed(1)},${y(p.value).toFixed(1)}`).join(" ");
  const area = `${line} L${xs[xs.length - 1].toFixed(1)},${(H - P).toFixed(1)} L${xs[0].toFixed(1)},${(H - P).toFixed(1)} Z`;
  const end = vals[vals.length - 1];
  const pos = end >= 0;
  const col = pos ? "#2de0b4" : "#ff5d73";
  const fmt = (v) => `${v > 0 ? "+" : ""}${unit === "%" ? v.toFixed(1) : v}${unit}`;
  return (
    <div className="htv-chart-body">
      <div className="htv-chart-head"><span>{label}</span><b className={pos ? "htv-pos" : "htv-neg"}>{fmt(end)}</b></div>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="htv-chart-svg" role="img" aria-label={label}>
        <defs><linearGradient id="htv-roi-grad" x1="0" x2="0" y1="0" y2="1"><stop offset="0" stopColor={col} stopOpacity="0.32" /><stop offset="1" stopColor={col} stopOpacity="0" /></linearGradient></defs>
        <line x1="0" x2={W} y1={y(0)} y2={y(0)} className="htv-chart-zero" />
        <path d={area} fill="url(#htv-roi-grad)" />
        <path d={line} fill="none" stroke={col} strokeWidth="2" vectorEffect="non-scaling-stroke" />
        {points.map((p, i) => <circle key={i} cx={xs[i]} cy={y(p.value)} r="2.6" fill={col}><title>{p.day}: {fmt(p.value)}</title></circle>)}
      </svg>
      <div className="htv-chart-x">{points.map((p, i) => <span key={i}>{(p.day || "").slice(5)}</span>)}</div>
    </div>
  );
}

function summarize(picks) {
  const settled = picks.filter((p) => ["win", "loss", "push"].includes(p.result));
  const profit = Math.round(settled.reduce((a, p) => a + (p.profit || 0), 0) * 100) / 100;
  return { total: picks.length, settled: settled.length,
    wins: settled.filter((p) => p.result === "win").length, losses: settled.filter((p) => p.result === "loss").length,
    pushes: settled.filter((p) => p.result === "push").length, profit,
    roi: settled.length ? profit / settled.length : null, pending: picks.filter((p) => p.result == null).length };
}

// Streaks (current + longest win/loss) and best/worst day by profit, from a filtered pick set.
function streaksAndDays(picks) {
  const order = [...picks].filter((p) => p.result === "win" || p.result === "loss")
    .sort((a, b) => String(a.at || "").localeCompare(String(b.at || "")));
  let curType = null, curLen = 0, longWin = 0, longLoss = 0, run = 0, runType = null;
  order.forEach((p) => {
    if (p.result === runType) run += 1; else { runType = p.result; run = 1; }
    if (runType === "win") longWin = Math.max(longWin, run); else longLoss = Math.max(longLoss, run);
  });
  for (let i = order.length - 1; i >= 0; i--) {
    if (curType == null) { curType = order[i].result; curLen = 1; }
    else if (order[i].result === curType) curLen += 1; else break;
  }
  const byDay = {};
  picks.forEach((p) => {
    if (!p.day || !["win", "loss", "push"].includes(p.result)) return;
    const d = (byDay[p.day] = byDay[p.day] || { day: p.day, profit: 0, settled: 0 });
    d.profit += p.profit || 0; d.settled += 1;
  });
  const dayList = Object.values(byDay).map((d) => ({ ...d, profit: Math.round(d.profit * 100) / 100, roi: d.settled ? d.profit / d.settled : 0 }));
  let best = null, worst = null;
  dayList.forEach((d) => { if (!best || d.profit > best.profit) best = d; if (!worst || d.profit < worst.profit) worst = d; });
  return { curType, curLen, longWin, longLoss, best, worst, daysCount: dayList.length };
}

function CompareBox({ title, s, tone }) {
  return (
    <div className={`htv-cmp-box htv-cmp-${tone}`} data-testid={`htv-cmp-${tone}`}>
      <span className="htv-cmp-title">{title}</span>
      <b className={s.profit > 0 ? "htv-pos" : s.profit < 0 ? "htv-neg" : ""}>{s.settled ? `${s.profit > 0 ? "+" : ""}${s.profit}u` : "—"}</b>
      <span className="htv-cmp-sub">{s.wins}-{s.losses}{s.pushes ? `-${s.pushes}` : ""} · {s.roi == null ? "—" : `${(s.roi * 100).toFixed(0)}% ROI`}</span>
    </div>
  );
}

const HIST_COMPS = [{ key: "nba", comp: "", label: "NBA" }, { key: "E", comp: "E", label: "EuroLeague" }, { key: "U", comp: "U", label: "EuroCup" }];

function LiveHistory({ comp, league, onLeague, day }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [metric, setMetric] = useState("profit");
  const [market, setMarket] = useState(null);
  useEffect(() => {
    let on = true;
    setData(null); setErr(null); setMarket(null);
    api.get("/value/live-history", { params: comp ? { comp } : {} })
      .then((r) => on && setData(r.data)).catch((e) => on && setErr(errorText(e)));
    return () => { on = false; };
  }, [comp]);

  const allPicks = useMemo(() => (data?.days || []).flatMap((d) => d.picks), [data]);
  const picks = useMemo(() => (market ? allPicks.filter((p) => (p.market_label || "—") === market) : allPicks), [allPicks, market]);

  const exportCsv = () => {
    const head = ["Μέρα", "Τύπος", "Τίτλος", "Market", "Επιλογή", "Odds", "EV%", "Αποτέλεσμα", "Κέρδος(u)"];
    const res = (r) => r === "win" ? "Κέρδος" : r === "loss" ? "Ήττα" : r === "push" ? "Push" : r === "void" ? "Άκυρο" : "Εκκρεμεί";
    const esc = (v) => { const s = v == null ? "" : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const lines = [head.join(",")];
    allPicks.forEach((p) => lines.push([p.day, p.kind === "team" ? "Ομάδα" : "Παίκτης", p.title, p.market_label, p.label,
      p.odds != null ? Number(p.odds).toFixed(2) : "", p.ev != null ? (p.ev * 100).toFixed(1) : "",
      res(p.result), p.result && p.result !== "void" && p.profit != null ? p.profit : ""].map(esc).join(",")));
    const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `live-value-history-${(league || "nba")}-${nbaToday()}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };
  const printHref = `/value/print?comp=${encodeURIComponent(comp || "")}&start=${day || nbaToday()}&end=${day || nbaToday()}&phase=both`;
  const summary = useMemo(() => summarize(picks), [picks]);
  const stk = useMemo(() => streaksAndDays(picks), [picks]);
  const chartPoints = useMemo(() => {
    const byday = {};
    picks.forEach((p) => { (byday[p.day] = byday[p.day] || []).push(p); });
    let cumProfit = 0, cumSettled = 0; const pts = [];
    for (const d of Object.keys(byday).filter(Boolean).sort()) {
      const settled = byday[d].filter((p) => ["win", "loss", "push"].includes(p.result));
      if (!settled.length) continue;
      cumProfit = Math.round((cumProfit + settled.reduce((a, p) => a + (p.profit || 0), 0)) * 100) / 100;
      cumSettled += settled.length;
      pts.push({ day: d, profit: cumProfit, roi: cumSettled ? Math.round((cumProfit / cumSettled) * 1000) / 10 : 0 });
    }
    return pts;
  }, [picks]);

  const compBar = onLeague ? (
    <div className="htv-hist-comps" data-testid="htv-hist-comps">
      {HIST_COMPS.map((c) => (
        <button type="button" key={c.key} className={(league || "nba") === c.key ? "is-active" : ""}
          onClick={() => onLeague(c.key)} data-testid={`htv-hist-comp-${c.key}`}>{c.label}</button>
      ))}
    </div>
  ) : null;

  if (err) return <div className="htv-pane">{compBar}<p className="tv-empty">{err}</p></div>;
  if (!data) return <div className="htv-pane">{compBar}<p className="tv-empty">Φόρτωση ιστορικού…</p></div>;
  const s = summary;
  const resultTag = (r) => r === "win" ? ["Κέρδος", "win"] : r === "loss" ? ["Ήττα", "loss"] : r === "push" ? ["Push", "push"] : r === "void" ? ["Άκυρο", "void"] : ["Εκκρεμεί", "pending"];
  const days = market ? data.days.map((d) => ({ ...d, picks: d.picks.filter((p) => (p.market_label || "—") === market) })).filter((d) => d.picks.length) : data.days;
  const cmp = data.compare;

  return (
    <div className="htv-pane" data-testid="htv-history">
      {compBar}
      <div className="htv-hist-tools" data-testid="htv-hist-tools">
        <button type="button" className="htv-tool-btn" onClick={exportCsv} disabled={!allPicks.length} data-testid="htv-export-csv"><Download size={14} /> Εξαγωγή CSV</button>
        <Link className="htv-tool-btn htv-tool-print" to={printHref} target="_blank" rel="noopener noreferrer" data-testid="htv-print-link"><Printer size={14} /> Σελίδα εκτύπωσης</Link>
      </div>
      {market && <button type="button" className="htv-filter-tag" onClick={() => setMarket(null)} data-testid="htv-filter-clear">Φίλτρο: {market} ✕</button>}
      <div className="htv-sum" data-testid="htv-history-summary">
        <div><b>{s.total}</b><span>Picks</span></div>
        <div><b>{s.wins}-{s.losses}{s.pushes ? `-${s.pushes}` : ""}</b><span>Β-Η{s.pushes ? "-Π" : ""}</span></div>
        <div className={s.profit > 0 ? "htv-pos" : s.profit < 0 ? "htv-neg" : ""}><b>{s.profit > 0 ? "+" : ""}{s.profit}u</b><span>Κέρδος</span></div>
        <div><b>{s.roi == null ? "—" : `${(s.roi * 100).toFixed(1)}%`}</b><span>ROI</span></div>
        <div><b>{s.pending}</b><span>Εκκρεμή</span></div>
      </div>
      {s.settled > 0 && (
        <div className="htv-streaks" data-testid="htv-streaks">
          <div className="htv-streak" data-testid="htv-streak-current">
            <small>Τρέχον σερί</small>
            <b className={stk.curType === "win" ? "htv-pos" : stk.curType === "loss" ? "htv-neg" : ""}>
              {stk.curLen ? `${stk.curLen} ${stk.curType === "win" ? (stk.curLen === 1 ? "νίκη" : "νίκες") : (stk.curLen === 1 ? "ήττα" : "ήττες")}` : "—"}
            </b>
          </div>
          <div className="htv-streak" data-testid="htv-streak-best">
            <small>Μεγαλύτερα σερί</small>
            <b><span className="htv-pos">{stk.longWin}Β</span> · <span className="htv-neg">{stk.longLoss}Η</span></b>
          </div>
          <div className="htv-streak" data-testid="htv-day-best">
            <small>Καλύτερη μέρα</small>
            <b className="htv-pos">{stk.best ? `${stk.best.profit > 0 ? "+" : ""}${stk.best.profit}u` : "—"}</b>
            {stk.best && <em>{(stk.best.day || "").slice(5)} · {(stk.best.roi * 100).toFixed(0)}%</em>}
          </div>
          <div className="htv-streak" data-testid="htv-day-worst">
            <small>Χειρότερη μέρα</small>
            <b className="htv-neg">{stk.worst ? `${stk.worst.profit > 0 ? "+" : ""}${stk.worst.profit}u` : "—"}</b>
            {stk.worst && <em>{(stk.worst.day || "").slice(5)} · {(stk.worst.roi * 100).toFixed(0)}%</em>}
          </div>
        </div>
      )}
      {cmp && (cmp.pregame.settled > 0 || cmp.live.settled > 0) && (
        <div className="htv-compare" data-testid="htv-compare">
          <h4 className="htv-hday-title">Pregame vs Ημίχρονο</h4>
          <div className="htv-cmp-row">
            <CompareBox title="Pregame" s={cmp.pregame} tone="pre" />
            <CompareBox title="Ημίχρονο" s={cmp.live} tone="live" />
          </div>
        </div>
      )}
      {(data.by_market || []).length > 0 && (
        <div className="htv-bymkt" data-testid="htv-by-market">
          <h4 className="htv-hday-title">Απόδοση ανά market <span className="htv-hint">(κλικ για φίλτρο)</span></h4>
          <div className="htv-mkt-table">
            <div className="htv-mkt-head"><span>Market</span><span>Picks</span><span>Β-Η-Π</span><span>Κέρδος</span><span>ROI</span></div>
            {data.by_market.map((m) => (
              <button type="button" key={m.market_label} className={`htv-mkt-row ${market === m.market_label ? "is-active" : ""}`}
                onClick={() => setMarket(market === m.market_label ? null : m.market_label)} data-testid={`htv-mkt-${m.market_label}`}>
                <span className="htv-mkt-name">{m.market_label}</span>
                <span>{m.total}{m.pending ? ` · ${m.pending} εκκρ.` : ""}</span>
                <span>{m.wins}-{m.losses}{m.pushes ? `-${m.pushes}` : ""}</span>
                <span className={m.profit > 0 ? "htv-pos" : m.profit < 0 ? "htv-neg" : ""}>{m.settled ? `${m.profit > 0 ? "+" : ""}${m.profit}u` : "—"}</span>
                <span className={m.roi > 0 ? "htv-pos" : m.roi < 0 ? "htv-neg" : ""}>{m.roi == null ? "—" : `${(m.roi * 100).toFixed(0)}%`}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {chartPoints.length >= 2 && (
        <div className="htv-chart" data-testid="htv-roi-chart">
          <div className="htv-chart-toggle">
            <button type="button" className={metric === "profit" ? "is-active" : ""} onClick={() => setMetric("profit")} data-testid="htv-metric-profit">Κέρδος (u)</button>
            <button type="button" className={metric === "roi" ? "is-active" : ""} onClick={() => setMetric("roi")} data-testid="htv-metric-roi">ROI %</button>
          </div>
          <ProfitChart points={chartPoints.map((p) => ({ day: p.day, value: p[metric] }))} unit={metric === "roi" ? "%" : "u"}
            label={metric === "roi" ? "Πορεία ROI (%)" : "Πορεία κέρδους (units)"} />
        </div>
      )}
      {!days.length ? <p className="tv-empty">{market ? "Κανένα pick σε αυτό το market." : "Δεν υπάρχουν ακόμα live picks στο ιστορικό. Μόλις ανεβάσεις screenshot σε live αγώνα και βγουν value προτάσεις, θα αποθηκεύονται εδώ."}</p>
        : days.map((d) => (
          <section key={d.day} className="htv-hday" data-testid={`htv-hday-${d.day}`}>
            <h4 className="htv-hday-title">{d.day}</h4>
            {d.picks.map((p) => {
              const [label, cls] = resultTag(p.result);
              return (
                <div key={p.id} className="htv-hrow" data-testid={`htv-hrow-${p.id}`}>
                  <span className={`htv-kind htv-kind-${p.kind}`}>{p.kind === "team" ? "ΟΜ" : "ΠΑ"}</span>
                  <div className="htv-hmain">
                    <b>{p.title}</b>
                    <span>{p.market_label} · {p.label}{p.current != null ? ` · τότε ${p.current}` : ""}</span>
                  </div>
                  <span className="htv-hodds">@{(p.odds ?? 0).toFixed ? p.odds.toFixed(2) : p.odds}</span>
                  <span className={`htv-ev ${p.ev >= 0 ? "htv-pos" : "htv-neg"}`}>{evText(p.ev)}</span>
                  <span className={`htv-res htv-res-${cls}`} data-testid={`htv-res-${p.id}`}>{label}{p.profit != null && p.result && p.result !== "void" ? ` ${p.profit > 0 ? "+" : ""}${p.profit}u` : ""}</span>
                </div>
              );
            })}
          </section>
        ))}
    </div>
  );
}

function HalfTimePanel({ game, nba, comp, league, onLeague, day }) {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(() => {
    const m = params.get("mode");
    return ["players", "teams", "history"].includes(m) ? m : "players";
  });
  useEffect(() => {
    const m = params.get("mode");
    if (["players", "teams", "history"].includes(m)) setMode(m);
  }, [params]);
  const gameLabel = game ? `${game.away_code} @ ${game.home_code}` : "";
  // Scanners key off the selected game so a fresh pick isn't carried across matches.
  return (
    <section className="tv-panel" data-testid="htv-panel">
      <header className="tv-panel-head tv-players-head">
        <div className="tv-pills tv-mode">
          <button type="button" className={mode === "players" ? "is-active" : ""} onClick={() => setMode("players")} data-testid="htv-mode-players">Παίκτες</button>
          <button type="button" className={mode === "teams" ? "is-active" : ""} onClick={() => setMode("teams")} data-testid="htv-mode-teams">Ομάδες</button>
          <button type="button" className={mode === "history" ? "is-active" : ""} onClick={() => setMode("history")} data-testid="htv-mode-history">Ιστορικό</button>
        </div>
        {game && mode !== "history" && <span className="htv-current-game" data-testid="htv-current-game">{gameLabel}</span>}
      </header>
      <div className="tv-scroll">
        {mode === "players" && <PlayersMode key={`p-${comp}-${game?.id || "all"}`} comp={comp} gameId={game?.id} gameLabel={gameLabel} />}
        {mode === "teams" && <TeamsMode key={`t-${comp}-${game?.id || "all"}`} comp={comp} gameId={game?.id} gameLabel={gameLabel} />}
        {mode === "history" && <LiveHistory comp={comp} league={league} onLeague={onLeague} day={day} />}
      </div>
    </section>
  );
}

export default function HalfTimeValue() {
  const [day, setDay] = useState(nbaToday());
  const [league, setLeague] = useState("nba");
  const [gameId, setGameId] = useState(null);
  const { data, error } = useSlate(day, league);
  const nba = league === "nba";
  const games = useMemo(() => data?.games || [], [data]);
  useEffect(() => {
    if (data?.day !== day) return;
    if (games.length && !games.some((g) => g.id === gameId)) setGameId((games.find((g) => g.status === "in") || games[0]).id);
  }, [games, gameId, data, day]);
  const game = games.find((g) => g.id === gameId);

  const switchLeague = (l) => {
    setLeague(l); setGameId(null);
    if (l === "nba") { setDay(nbaToday()); return; }
    api.get("/euro/calendar", { params: { comp: l } }).then((r) => {
      const days = r.data.days || [];
      const today = nbaToday();
      const pick = [...days].reverse().find((d) => d <= today) || days[days.length - 1];
      if (pick) setDay(pick);
    }).catch(() => {});
  };

  return (
    <div className="tv-root" data-testid="halftime-value">
      <div className="tv-daybar">
        <span className="htv-overline">Live · Ημίχρονο</span>
        <button type="button" onClick={() => setDay(shift(day, -1))} aria-label="Previous day" data-testid="htv-prev-day"><ChevronLeft size={16} /></button>
        <span data-testid="htv-day">{day === nbaToday() ? "Today · " : ""}{shortDate(day)}</span>
        <button type="button" onClick={() => setDay(shift(day, 1))} aria-label="Next day" data-testid="htv-next-day"><ChevronRight size={16} /></button>
        {error && <span className="tv-error">{error}</span>}
      </div>
      <div className="tv-grid">
        <GamesPanel games={games} selectedId={gameId} onSelect={setGameId} league={league} onLeague={switchLeague} />
        <HalfTimePanel game={game} nba={nba} comp={nba ? "" : league} league={league} onLeague={switchLeague} day={day} />
        {nba ? <GameCenter game={game} /> : <EuroGameCenter game={game} comp={league} odds={null} />}
      </div>
    </div>
  );
}
